import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { suretyClient } from './network.js';
import { useToast } from '../../components/ui/Toast.js';
import type { NomineeDocument } from './types.js';

export const suretyKeys = {
  all: ['surety'] as const,
  lists: () => [...suretyKeys.all, 'list'] as const,
  list: (filters: any) => [...suretyKeys.lists(), filters] as const,
  details: () => [...suretyKeys.all, 'detail'] as const,
  detail: (id: string) => [...suretyKeys.details(), id] as const,
};

export function useSuretyRequests(filters: { schemeId?: string }) {
  return useQuery({
    queryKey: suretyKeys.list(filters),
    queryFn: () => suretyClient.listRequests(filters),
  });
}

export function useSuretyRequestDetail(id: string) {
  return useQuery({
    queryKey: suretyKeys.detail(id),
    queryFn: () => suretyClient.getRequestDetail(id),
    enabled: !!id,
  });
}

export function useAddNominee() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, body }: { id: string; body: { nomineeCustomerId: string; nomineeSubscriberId?: string } }) =>
      suretyClient.addNominee(id, body),
    onSuccess: (_, { id }) => {
      void qc.invalidateQueries({ queryKey: suretyKeys.detail(id) });
    },
  });
}

export function useUploadSuretyDoc() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ nomineeId, documents }: { requestId: string; nomineeId: string; documents: NomineeDocument[] }) =>
      suretyClient.uploadDocuments(nomineeId, documents),
    onSuccess: (_, { requestId }) => {
      void qc.invalidateQueries({ queryKey: suretyKeys.detail(requestId) });
      toast({
        type: 'success',
        title: 'Document Uploaded',
        description: 'Nominee document was uploaded and status updated to DOCUMENTS_SUBMITTED.',
      });
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        title: 'Upload Failed',
        description: err?.message || 'Failed to upload nominee document.',
      });
    },
  });
}

export function useApproveNominee() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ requestId, nomineeId }: { requestId: string; nomineeId: string }) =>
      suretyClient.approveNominee(nomineeId),
    onSuccess: (_, { requestId }) => {
      void qc.invalidateQueries({ queryKey: suretyKeys.detail(requestId) });
      toast({
        type: 'success',
        title: 'Nominee Approved',
        description: 'The nominee has been successfully approved.',
      });
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        title: 'Approval Failed',
        description: err?.message || 'Failed to approve nominee.',
      });
    },
  });
}

export function useRejectNominee() {
  const qc = useQueryClient();
  const { toast } = useToast();
  return useMutation({
    mutationFn: ({ requestId, nomineeId, rejectionReason }: { requestId: string; nomineeId: string; rejectionReason: string }) =>
      suretyClient.rejectNominee(nomineeId, rejectionReason),
    onSuccess: (_, { requestId }) => {
      void qc.invalidateQueries({ queryKey: suretyKeys.detail(requestId) });
      toast({
        type: 'success',
        title: 'Nominee Rejected',
        description: 'The nominee has been rejected.',
      });
    },
    onError: (err: any) => {
      toast({
        type: 'error',
        title: 'Rejection Failed',
        description: err?.message || 'Failed to reject nominee.',
      });
    },
  });
}
